import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { cn } from "@/lib/utils";

/**
 * Typography Component - Simple wrapper for text elements (no Material-UI)
 * @param {*} props
 */
const TypographyComponent = forwardRef((props, ref) => {
  const {
    variant,
    component,
    className,
    classes,
    color,
    align,
    gutterBottom,
    noWrap,
    paragraph,
    children,
    style,
    title,
    onClick,
    contentEditable,
    onBlurChange,
    placeHolder,
    ...rest
  } = props;

  // Handle contentEditable props
  const accessProps = contentEditable && onBlurChange ? {
    contentEditable,
    onBlur: (e) => onBlurChange(e.target.textContent),
    "data-placeholder": placeHolder
  } : {};

  // Map variant to HTML element
  const getComponent = () => {
    if (component) return component;
    switch (variant) {
      case "h1":
        return "h1";
      case "h2":
        return "h2";
      case "h3":
        return "h3";
      case "h4":
        return "h4";
      case "h5":
        return "h5";
      case "h6":
        return "h6";
      case "subtitle1":
      case "subtitle2":
        return "h6";
      case "body1":
      case "body2":
        return "p";
      case "caption":
      case "overline":
        return "span";
      case "button":
        return "span";
      default:
        return paragraph ? "p" : "span";
    }
  };

  // Map variant to Tailwind classes
  const getVariantClasses = () => {
    switch (variant) {
      case "h1":
        return "text-4xl font-bold";
      case "h2":
        return "text-3xl font-bold";
      case "h3":
        return "text-2xl font-semibold";
      case "h4":
        return "text-xl font-semibold";
      case "h5":
        return "text-lg font-medium";
      case "h6":
        return "text-base font-medium";
      case "subtitle1":
        return "text-base font-normal";
      case "subtitle2":
        return "text-sm font-medium";
      case "body1":
        return "text-base font-normal";
      case "body2":
        return "text-sm font-normal";
      case "caption":
        return "text-xs font-normal";
      case "overline":
        return "text-xs font-normal uppercase tracking-wider";
      case "button":
        return "text-sm font-medium uppercase";
      default:
        return "";
    }
  };

  // Map alignment
  const getAlignClasses = () => {
    switch (align) {
      case "left":
        return "text-left";
      case "center":
        return "text-center";
      case "right":
        return "text-right";
      case "justify":
        return "text-justify";
      default:
        return "";
    }
  };

  // Combine all classes
  const combinedClassName = cn(
    getVariantClasses(),
    getAlignClasses(),
    gutterBottom && "mb-2",
    noWrap && "whitespace-nowrap overflow-hidden text-ellipsis",
    classes?.root,
    className
  );

  const Component = getComponent();

  return (
    <Component 
      ref={ref}
      className={combinedClassName} 
      style={style} 
      onClick={onClick}
      {...accessProps}
      {...rest}
    >
      {title}{children}
    </Component>
  );
});

// Default props
TypographyComponent.defaultProps = {
  variant: "body1",
  component: null,
  className: "",
  classes: {},
  color: "primary",
  align: "left",
  gutterBottom: false,
  noWrap: false,
  paragraph: false,
  title: "",
  placeHolder: "Enter Here",
  onClick: () => {},
  onBlurChange: () => {},
};

// Prop types
TypographyComponent.propTypes = {
  variant: PropTypes.oneOf([
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "subtitle1",
    "subtitle2",
    "body1",
    "body2",
    "caption",
    "button",
    "overline",
  ]),
  component: PropTypes.elementType,
  className: PropTypes.string,
  classes: PropTypes.object,
  color: PropTypes.string,
  align: PropTypes.oneOf(["inherit", "left", "center", "right", "justify"]),
  gutterBottom: PropTypes.bool,
  noWrap: PropTypes.bool,
  paragraph: PropTypes.bool,
  children: PropTypes.any,
  style: PropTypes.object,
  title: PropTypes.string,
  onClick: PropTypes.func,
  contentEditable: PropTypes.bool,
  onBlurChange: PropTypes.func,
  placeHolder: PropTypes.string,
};

/** Export Component */
export const Typography = TypographyComponent;
